/**
 * About Hero — Scroll-based block highlight animation
 *
 * Figma hierarchy reflected in this file:
 * ─────────────────────────────────────────────────
 *   Hero
 *     └── Section wrapper
 *           └── Columns grid
 *                 ├── About You block  [data-block="you"]
 *                 └── About US block   [data-block="us"]
 *
 * How the animation works
 * ─────────────────────────────────────────────────
 * We track the section's position relative to the viewport on every scroll
 * tick and compute a normalised progress value (0 → 1):
 *
 *   progress = 0   section top  just entered the viewport bottom
 *   progress = 1   section bottom just left  the viewport top
 *
 * That progress drives a cross-fade between the two blocks:
 *
 *   progress 0.00 → 0.38   "You" fully bright (1),  "US" dimmed
 *   progress 0.38 → 0.62   smooth lerp cross-fade between the two
 *   progress 0.62 → 1.00   "You" dimmed,            "US" fully bright (1)
 *
 * The dim opacity value is read from the CSS custom property
 * --about-hero-col-opacity-dim, so it can be tuned in the stylesheet
 * without touching this file.
 *
 * Opacity is applied via style.setProperty('--col-opacity', value) on each
 * block element. The CSS file picks that variable up:
 *   .about-hero__block { opacity: var(--col-opacity, 1); }
 *
 * Reduced motion
 * ─────────────────────────────────────────────────
 * When the OS reports prefers-reduced-motion: reduce, the JS exits early
 * and both blocks remain at full opacity (as set by the CSS fallback).
 */

if (!customElements.get('about-hero')) {
  customElements.define(
    'about-hero',
    class AboutHero extends HTMLElement {
      /** @type {HTMLElement|null} Block for "About You" (left / top) */
      blockYou = null;

      /** @type {HTMLElement|null} Block for "About US" (right / bottom) */
      blockUs = null;

      /** @type {number} requestAnimationFrame handle */
      rafId = 0;

      /** @type {boolean} */
      prefersReducedMotion = false;

      constructor() {
        super();
      }

      connectedCallback() {
        // Resolve the two block elements using data-block hooks.
        this.blockYou = this.querySelector('[data-block="you"]');
        this.blockUs = this.querySelector('[data-block="us"]');

        if (!this.blockYou || !this.blockUs) return;

        // Bail out entirely when the user prefers reduced motion.
        // The CSS fallback (opacity: var(--col-opacity, 1)) keeps both
        // blocks fully visible — no JS override needed.
        this.prefersReducedMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

        if (this.prefersReducedMotion) return;

        // Paint the initial state synchronously before the first scroll event:
        //   About You → fully highlighted
        //   About US  → dimmed
        this.applyOpacities(1, this.getDimOpacity());

        // Attach scroll listener. passive:true tells the browser we will
        // never call preventDefault(), allowing paint-thread optimisations.
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });

        // Run once immediately — the section may already be in the viewport
        // on page load (e.g. when linked directly to the about page).
        this.scheduleUpdate();
      }

      disconnectedCallback() {
        // Clean up to avoid memory leaks when the element is removed.
        window.removeEventListener('scroll', this.onScroll);
        cancelAnimationFrame(this.rafId);
      }

      // ── Event handler ─────────────────────────────────────────────────────

      onScroll() {
        this.scheduleUpdate();
      }

      // ── Core animation logic ──────────────────────────────────────────────

      /**
       * Coalesce multiple scroll events into a single rAF callback so DOM
       * writes never happen more than once per frame.
       */
      scheduleUpdate() {
        cancelAnimationFrame(this.rafId);
        this.rafId = requestAnimationFrame(() => this.update());
      }

      /**
       * Compute scroll progress and update block opacities.
       *
       * Progress formula:
       *   totalTravel = section.height + viewport.height
       *   scrolled    = viewport.height - section.getBoundingClientRect().top
       *   progress    = clamp(scrolled / totalTravel, 0, 1)
       *
       * This maps the full journey of the section through the viewport to [0,1].
       */
      update() {
        const rect = this.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        const totalTravel = rect.height + viewportHeight;
        const scrolled = viewportHeight - rect.top;

        // Normalise and clamp to [0, 1].
        const progress = Math.min(Math.max(scrolled / totalTravel, 0), 1);

        const dim = this.getDimOpacity();

        // Cross-fade band boundaries.
        const fadeStart = 0.38;
        const fadeEnd = 0.62;

        let opacityYou;
        let opacityUs;

        if (progress <= fadeStart) {
          // ── Phase 1: About You fully lit, About US dimmed ──────────────
          opacityYou = 1;
          opacityUs = dim;
        } else if (progress >= fadeEnd) {
          // ── Phase 3: About You dimmed, About US fully lit ───────────────
          opacityYou = dim;
          opacityUs = 1;
        } else {
          // ── Phase 2: cross-fade — interpolate smoothly ──────────────────
          // t goes 0→1 across the fade band.
          const t = (progress - fadeStart) / (fadeEnd - fadeStart);
          opacityYou = this.lerp(1, dim, t);
          opacityUs = this.lerp(dim, 1, t);
        }

        this.applyOpacities(opacityYou, opacityUs);
      }

      // ── Helpers ───────────────────────────────────────────────────────────

      /**
       * Write opacity values onto each block via the --col-opacity CSS variable.
       * The CSS file consumes this variable:  opacity: var(--col-opacity, 1)
       * Keeping it as a CSS variable means no inline style="" attributes are
       * written to the HTML — consistent with the "no inline styles" rule.
       *
       * @param {number} you - Opacity for the "About You" block (0–1).
       * @param {number} us  - Opacity for the "About US" block (0–1).
       */
      applyOpacities(you, us) {
        this.blockYou.style.setProperty('--col-opacity', you.toFixed(3));
        this.blockUs.style.setProperty('--col-opacity', us.toFixed(3));
      }

      /**
       * Read the dim opacity from the section's CSS custom property so the
       * value lives in one place (the stylesheet) and JS stays in sync
       * automatically.
       *
       * @returns {number}
       */
      getDimOpacity() {
        const raw = getComputedStyle(this)
          .getPropertyValue('--about-hero-col-opacity-dim')
          .trim();
        const parsed = parseFloat(raw);
        return Number.isFinite(parsed) ? parsed : 0.28;
      }

      /**
       * Linear interpolation between two numbers.
       *
       * @param {number} a - Start value.
       * @param {number} b - End value.
       * @param {number} t - Progress factor (0–1).
       * @returns {number}
       */
      lerp(a, b, t) {
        return a + (b - a) * t;
      }
    }
  );
}
